import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import { isEmpty, startCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getCurrentAccount } from '../../store/selectors/accountSelectors';
import {
  selectBalancesAvailableForTrading,
  selectBalancesAvailableForWithdrawal,
} from '../../store/selectors/portfolioBalancesSelectors';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';
import {
  productLocationTypes,
  productLocationUSType,
} from '../../util/enum/api/productTypes';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import {
  selectBuyCashBufferPercentage,
  selectSettings,
} from '../../store/selectors/settingsSelectors';
import { verifyBuyForDelivery } from '../../store/actions/orders/orderActions';
import BuyForDeliveryCalculation from './BuyForDeliveryCalculation';

import Note from '../Notes/Note';
import Label from '../Notes/Label';
import ModalBody from '../Modal/ModalBody';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  ecommerceGtmHelper,
  onConfirmOrderEcommerceGtmHelper,
  // orderGtmHelper,
} from '../../util/helpers/gtmHelper';
import { selectIsLoadingByActionTypes } from '../../store/selectors/loadingSelectors';
import { VERIFY_BUY_FOR_DELIVERY_LOADING } from '../../store/actions/orders/orderActionConstants';
import SectionLoader from '../Loader/SectionLoader';
import { PORTFOLIO_BALANCES_LOADING } from '../../store/actions/portfolio/portfolioActionConstants';
import { parseEnumType } from '../../util/helpers/enumMappers';
import useProductPrice from '../../util/hooks/useProductPrice';
import BuyFields from '../InputFields/BuyFields';
import { buyForDeliveryValidationSchema } from '../../validation/buyForDeliveryValidationSchema';
import ModalTitle from '../Modal/ModalTitle';
import TwoColumnList from '../TwoColumnList/TwoColumnList';
import Paragraph from '../Paragraph/Paragraph';
import ModalButtons from '../Modal/ModalButtons';
import SingleColumnList from '../TwoColumnList/SingleColumnList';
import SupportModal from '../Modals/SupportModal/SupportModal';
import config from '../../config';
import themeColors from '../../assets/styles/themeColors';
import { pxToRem } from '../../assets/styles/helper';
// import { ADD_TO_CART, SELECT_ITEM } from '../../constants/gtmEvents';

const Error = styled.div`
  dispay: flex;
  color: ${themeColors.colorRed};
  font-size: ${pxToRem(16)};
  line-height: 1.35;
  font-weight: 500;
`;

const OrderForDelivery = ({
  handleNext,
  setWizardData,
  wizardData,
  handleBack,
  setWizardTitle,
  tiers,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const {
    // gtmCustomEvent,
    gtmDialogScreenView,
    ecommerceGtmEvent,
  } = useGtmHook();
  const [activeType, setActiveType] = useState(wizardData.activeType || 'Cash');
  const [deliveryFees, setDeliveryFees] = useState({});
  const [limitation, setLimitation] = useState(0);
  const limitValue = 225000;
  const showLimitationMsg = limitation >= limitValue;
  const [isSupportModalOpen, setSupportModalOpen] = useState(false);
  const [isLockedPriceUser, setIsLockedPriceUser] = useState(false);
  const [
    balanceAvailableForTradingIsValid,
    setBalanceAvailableForTradingIsValid,
  ] = useState(true);
  const [
    orderAvailableForTradingIsValid,
    setOrderIsAvailableForTradingIsValid,
  ] = useState(true);

  const dispatch = useDispatch();
  const account = useSelector(getCurrentAccount);
  const balanceAvailableForTrading = useSelector(
    selectBalancesAvailableForTrading,
  );
  const balancesAvailableForWithdrawal = useSelector(
    selectBalancesAvailableForWithdrawal,
  );
  const buyCashBuffer = useSelector(selectBuyCashBufferPercentage);
  const unitPrice = useProductPrice(
    wizardData?.storageProduct,
    wizardData?.selectedVault,
  );
  const [pricePerUnit, setPricePerUnit] = useState(unitPrice);
  const settings = useSelector(selectSettings);

  const isLoading = useSelector(
    selectIsLoadingByActionTypes([
      PORTFOLIO_BALANCES_LOADING,
      VERIFY_BUY_FOR_DELIVERY_LOADING,
    ]),
  );

  const {
    storageProduct,
    selectedVault,
    cashBuffer,
    deliveryAddress,
  } = wizardData;

  const isLegacyPM = () => {
    const { brokerTheme } = config;
    if (brokerTheme === 'LegacyPM') {
      return true;
    }
    return false;
  };

  const getMinimumOrderAmount = () => {
    const buyForDeliveryLocationId = settings.BuyForDeliveryLocation;

    const vaultWithDeliveryMinimum = settings.BrokerOrderAmountMinimums.find(
      (item) =>
        startCase(parseEnumType(productLocationTypes, item.Location)) ===
        deliveryAddress?.StateRegion,
    );

    const legacyPMVaultWithDeliveryMinimum = settings.BrokerOrderAmountMinimums.find(
      (item) => item.Location === buyForDeliveryLocationId,
    );

    if (!vaultWithDeliveryMinimum && !isLegacyPM()) {
      return 0;
    }

    if (vaultWithDeliveryMinimum && !isLegacyPM()) {
      const { BuyForDeliveryAmount } = vaultWithDeliveryMinimum;
      return BuyForDeliveryAmount;
    }

    if (isLegacyPM() && legacyPMVaultWithDeliveryMinimum) {
      const { BuyForDeliveryAmount } = legacyPMVaultWithDeliveryMinimum;
      return BuyForDeliveryAmount;
    }
  };

  const addressValidation = () => {
    const locationStateRegion = storageProduct.Locations.some((location) =>
      productLocationUSType.includes(
        parseEnumType(productLocationTypes, location.LocationType),
      ),
    );

    return locationStateRegion && deliveryAddress?.CountryIso3Code === 'USA';
  };

  useEffect(() => {
    if (account.AccountUid) {
      dispatch(fetchPortfolioBalances({ accountUid: account.AccountUid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.AccountUid]);

  useEffect(() => {
    setWizardTitle(t('buyWizard.buyForDelivery.orderEntry.modalTitle'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (wizardData?.storageProduct) {
      return;
    }

    gtmDialogScreenView({
      title: `Buy for Delivery - Order Entry ${wizardData?.storageProduct.ProductCaption}`,
    });
  }, []); //eslint-disable-line

  useEffect(() => {
    if (unitPrice) {
      setPricePerUnit(unitPrice);
      ecommerceGtmHelper({
        ecommerceGtmEvent,
        // eventName: SELECT_ITEM,
        wizardData,
        orderSideType: 'buy',
        account,
        unitPrice,
        isForDelivery: true,
      });
    }
  }, [unitPrice]); //eslint-disable-line

  useEffect(() => {
    setWizardData({
      ...wizardData,
      balanceAvailable: balanceAvailableForTrading,
      cashBuffer: buyCashBuffer,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceAvailableForTrading, buyCashBuffer]);

  useEffect(() => {
    if (
      !settings ||
      isEmpty(settings) ||
      !settings.BrokerCode ||
      !wizardData?.calculation ||
      !balanceAvailableForTrading
    ) {
      return;
    }

    setIsLockedPriceUser(settings?.IsLockedPricingActive);
    const { calculation } = wizardData;
    const isValid =
      activeType === 'Quantity' && !settings?.IsLockedPricingActive
        ? balanceAvailableForTrading >=
          calculation?.estimatedOrderTotalWithCashBuffer
        : balanceAvailableForTrading >= calculation?.estimatedOrderTotal;

    setBalanceAvailableForTradingIsValid(isValid);
  }, [activeType, balanceAvailableForTrading, settings, wizardData]);

  useEffect(() => {
    if (
      !wizardData ||
      !wizardData?.calculation ||
      !wizardData?.calculation?.newPricePerUnit
    ) {
      return;
    }
    setPricePerUnit(wizardData?.calculation?.newPricePerUnit);
  }, [wizardData]);

  if (isEmpty(wizardData)) {
    return null;
  }

  const displayError = () => {
    const isSelectedAddressEnabled = addressValidation();
    if (!isSelectedAddressEnabled) {
      return <Error>{t('buyWizard.buyForDelivery.locationValidation')}</Error>;
    }
  };

  const handleVerifyOrder = (values, setFieldError) => {
    const isSelectedAddressEnabled = addressValidation();
    if (!isSelectedAddressEnabled) {
      return;
    }

    const onSuccess = (deliveryOrderFees) => {
      setDeliveryFees({ ...deliveryOrderFees });
      setWizardData({ ...wizardData, ...deliveryOrderFees });
    };

    const onError = (errorMessage) => {
      if (activeType === 'Cash') {
        setFieldError('CashAmount', errorMessage);
      }

      setFieldError('QuantityAmount', errorMessage);
    };

    dispatch(
      verifyBuyForDelivery({
        accountUid: account.AccountUid,
        cashAmount:
          activeType === 'Cash' ? values.CashAmount : values.QuantityAmount,
        data: {
          AccountUid: account.AccountUid,
          QuantityAmount:
            activeType === 'Cash' ? values.CashAmount : values.QuantityAmount,
          AddressUid: deliveryAddress?.AddressUid,
          Type: 'BuyForDelivery',
          SymbolCode: selectedVault?.SymbolCode,
          UnitType: activeType,
        },
        onSuccess,
        onError,
      }),
    );
  };

  const onActiveTypeChange = (value) => {
    const { CashAmount, QuantityAmount } = ref.current.values;
    if (value === 'Cash') {
      setLimitation(CashAmount);
    } else {
      setLimitation(unitPrice * QuantityAmount);
    }
    setActiveType(value);
    if (deliveryFees) {
      setDeliveryFees({});
    }
  };

  const submitForm = (values, { setFieldError }) => {
    if (isEmpty(deliveryFees)) {
      handleVerifyOrder(values, setFieldError);
      return;
    }

    setWizardData({
      ...wizardData,
      orderType: activeType,
      orderValues: values,
    });

    // orderGtmHelper(
    //   gtmDialogScreenView,
    //   gtmCustomEvent,
    //   values,
    //   wizardData,
    //   activeType,
    //   unitPrice,
    //   currentAccount,
    //   true,
    // );

    onConfirmOrderEcommerceGtmHelper({
      ecommerceGtmEvent,
      // eventName: ADD_TO_CART,
      wizardData,
      orderSideType: 'buy',
      account,
      unitPrice,
      activeType,
      values,
      isForDelivery: true,
    });
    handleNext();
  };

  const balanceAvailableForWithdrawalIsValid =
    balancesAvailableForWithdrawal >=
    (wizardData?.calculation?.deliveryCosts || 0) +
      (wizardData?.calculation?.salesTax || 0);

  return (
    <>
      <SectionLoader isLoading={isLoading}>
        <ModalBody>
          <ModalTitle marginBottom={4}>
            {storageProduct?.ProductCaption}
          </ModalTitle>
          <Label marginBottom={32} text={t('buyWizard.common.orderEntry')} />
          <TwoColumnList marginBottom={24}>
            <SingleColumnList>
              <Label
                marginBottom={4}
                text={
                  settings?.IsLockedPricingActive
                    ? t('buyWizard.common.pricePerUnitLockedPrice')
                    : t('buyWizard.common.pricePerUnit')
                }
              />
              <Paragraph fontSize={18}>
                {formatMoneyNumeral(pricePerUnit)}
              </Paragraph>
            </SingleColumnList>
            <SingleColumnList>
              <Label
                marginBottom={4}
                text={t('buyWizard.common.availableForTrading')}
              />
              <Paragraph fontSize={18}>
                {formatMoneyNumeral(balanceAvailableForTrading)}
              </Paragraph>
            </SingleColumnList>
          </TwoColumnList>
          <Formik
            innerRef={ref}
            initialValues={{
              CashAmount: wizardData?.orderValues?.CashAmount || '',
              QuantityAmount: wizardData?.orderValues?.QuantityAmount || '',
            }}
            initialTouched={{
              CashAmount: true,
              QuantityAmount: true,
            }}
            validationSchema={buyForDeliveryValidationSchema({
              activeType,
              unitPrice,
              balanceAvailableForTrading,
              getMinimumOrderAmount,
              deliveryAddress,
            })}
            onSubmit={submitForm}
            validateOnBlur={false}
          >
            {({ values, setFieldValue, setFieldTouched }) => {
              const isNextButtonEnabled = addressValidation();
              return (
                <Form>
                  <BuyFields
                    activeType={activeType}
                    setActiveType={onActiveTypeChange}
                    values={values}
                    setFieldValue={setFieldValue}
                    selectedVault={selectedVault}
                    setFieldTouched={setFieldTouched}
                    setDeliveryFees={setDeliveryFees}
                    deliveryFees={deliveryFees}
                    unitPrice={unitPrice}
                    setLimitation={setLimitation}
                    displayError={displayError}
                    setOrderIsAvailableForTradingIsValid={
                      setOrderIsAvailableForTradingIsValid
                    }
                    orderAvailableForTradingIsValid={
                      orderAvailableForTradingIsValid
                    }
                  />

                  {!isEmpty(deliveryFees) && (
                    <BuyForDeliveryCalculation
                      activeType={activeType}
                      values={values}
                      cashBuffer={cashBuffer}
                      unitPrice={unitPrice}
                      setWizardData={setWizardData}
                      wizardData={wizardData}
                      deliveryFees={deliveryFees}
                      balanceAvailableForTradingIsValid={
                        balanceAvailableForTradingIsValid
                      }
                      balanceAvailableForWithdrawalIsValid={
                        balanceAvailableForWithdrawalIsValid
                      }
                      tiers={tiers}
                      isLockedPriceUser={isLockedPriceUser}
                      setPricePerUnit={setPricePerUnit}
                      isProductPage
                    />
                  )}

                  <Paragraph marginTop={24} isColumn>
                    {activeType === 'Cash' && (
                      <Note
                        marginBottom={8}
                        text={t('buyWizard.buyForDelivery.orderEntry.cashNote')}
                      />
                    )}
                    {settings &&
                      !isEmpty(settings) &&
                      !settings?.IsLockedPricingActive && (
                        <Note text={t('buyWizard.common.note')} />
                      )}
                  </Paragraph>
                  {showLimitationMsg && (
                    <Paragraph isError marginTop={8}>
                      {t('buyWizard.buyForDelivery.limitationCopy')}
                    </Paragraph>
                  )}
                  {!showLimitationMsg && (
                    <ModalButtons
                      marginTop
                      isHorizontal
                      primaryButtonProps={{
                        type: 'submit',
                        label: isEmpty(deliveryFees)
                          ? t('common.verify')
                          : t('common.next'),
                        disabled:
                          (!isEmpty(deliveryFees) &&
                            (!balanceAvailableForTradingIsValid ||
                              !balanceAvailableForWithdrawalIsValid ||
                              (selectedVault?.IsCashOrderOnly &&
                                activeType === 'Quantity'))) ||
                          !isNextButtonEnabled,
                      }}
                      secondaryButtonProps={{ onClick: handleBack }}
                    />
                  )}

                  {showLimitationMsg && (
                    <ModalButtons
                      marginTop
                      isHorizontal
                      primaryButtonProps={{
                        label: t('common.contact'),
                        disabled: !isNextButtonEnabled,
                        onClick: () => setSupportModalOpen(true),
                      }}
                      secondaryButtonProps={{ onClick: handleBack }}
                    />
                  )}
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </SectionLoader>

      <SupportModal
        handleOutsideClick
        isOpen={isSupportModalOpen}
        close={setSupportModalOpen}
      />
    </>
  );
};

OrderForDelivery.propTypes = {
  handleNext: PropTypes.func,
  setWizardData: PropTypes.func,
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      Locations: PropTypes.arrayOf({
        LocationType: PropTypes.string,
      }),
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
      IsCashOrderOnly: PropTypes.bool,
    }),
    cashBuffer: PropTypes.number,
    calculation: PropTypes.shape({
      estimatedOrderTotalWithCashBuffer: PropTypes.number,
      estimatedSubTotal: PropTypes.number,
      estimatedOrderTotal: PropTypes.number,
      deliveryCosts: PropTypes.number,
      salesTax: PropTypes.number,
      unassessedStorageFees: PropTypes.number,
      newPricePerUnit: PropTypes.number,
    }),
    deliveryAddress: PropTypes.shape({
      AddressUid: PropTypes.string,
      StateRegion: PropTypes.string,
      CountryIso3Code: PropTypes.string,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    activeType: PropTypes.string,
  }),
  handleBack: PropTypes.func,
  setWizardTitle: PropTypes.func,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      HighBound: PropTypes.number,
      LowBound: PropTypes.number,
      PricePerOz: PropTypes.number,
      PricePerUnit: PropTypes.number,
    }),
  ),
};

export default OrderForDelivery;
